import styled from 'styled-components';

import { ANIM, COLORS } from '../../../../theme/tokens';
import { Text } from '../../../../components';

// The modal shell (backdrop, panel, portal, close button, header divider) now comes
// from the shared <Sheet> (@/nova/components/Sheet); the styles
// below are only the modal's own content: the rich header cluster, payment options,
// total row and footnote.

// --- Header ---------------------------------------------------------------------
export const HeaderId = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

export const IconTile = styled.div`
  flex: 0 0 auto;
  width: 65px;
  height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: ${COLORS.modalTileBg};
`;

export const IconArt = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Name = styled(Text)`
  font-size: 24px;
  color: ${COLORS.textSubtle};
  white-space: nowrap;
`;

export const TierPill = styled(Text)`
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  background: ${COLORS.tierPillBg};
  color: ${COLORS.ctaPillBorder};
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Price = styled(Text)`
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  color: ${COLORS.textBrightest};
`;

export const DiscountCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.15;
`;

export const Savings = styled(Text)`
  font-size: 10px;
  color: ${COLORS.benefitPositive};
`;

export const PrevPrice = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  text-decoration: line-through;
  color: ${COLORS.textGhost};
`;

export const Subtitle = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.textBrightest};
`;

// --- Payment options --------------------------------------------------------------
export const OptionCard = styled.div`
  /* Borderless flat card: the surfaceFaint grey fill alone reads it as a card on
   * both themes (surfaceSoft would derive to white and vanish on light without an
   * edge). Matches the studio's borderless-card convention. */
  border-radius: 16px;
  background: ${COLORS.surfaceFaint};
  overflow: hidden;
`;

export const OptionRow = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  /* Dims the row (radio + label + hint) when the option is unavailable. The note
   * below sits outside the row so it stays fully legible. */
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  &:disabled {
    cursor: not-allowed;
  }
`;

/** Insufficient-funds note under a disabled option (e.g. wallet balance too low).
 *  Padded to align under the option label (row pad 16 + radio 18 + gap 10). */
export const OptionNote = styled(Text)`
  display: block;
  padding: 0 16px 16px 44px;
  font-size: 11px;
  line-height: 1.3;
  color: ${COLORS.warning};
`;

export const Radio = styled.div<{ $selected: boolean }>`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: ${(p) => (p.$selected ? 'none' : `1.5px solid ${COLORS.radioBorder}`)};
  background: ${(p) => (p.$selected ? COLORS.ctaPillBorder : 'transparent')};
`;

export const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const OptionLabel = styled(Text)`
  font-size: 13px;
  color: ${COLORS.optionLabel};
`;

export const OptionHint = styled(Text)`
  font-size: 11px;
  color: ${COLORS.optionHint};
`;

/** Animated reveal for the Stripe payment methods (0fr → 1fr row trick). */
export const OptionDrawer = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows ${ANIM.DRAWER_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
`;

export const OptionDrawerInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const StripeArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 8px 16px;
`;

export const StripeSectionLabel = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textSubtle};
`;

/** Status line inside the card drawer while the activation invoice's Stripe intent is being
 *  prepared (after the first "Pay" click), or when it couldn't be created / a confirm failed.
 *  Amber, matching the wallet-short note, so it reads as advisory — the wallet path is always
 *  the reliable alternative. */
export const CardStatusNote = styled(Text)`
  display: block;
  padding: 0 8px 12px;
  font-size: 12px;
  line-height: 1.35;
  color: ${COLORS.warning};
`;

// --- Footer -------------------------------------------------------------------------
export const TotalRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 16px 0;
`;

export const TotalLabel = styled(Text)`
  font-size: 22px;
  line-height: 24px;
  color: ${COLORS.featureName};
`;

export const TotalCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

export const TotalPrice = styled.div`
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  color: ${COLORS.onAccent};
`;

export const TotalNote = styled(Text)`
  font-size: 11px;
  line-height: 1.1;
  text-align: right;
  color: ${COLORS.textGhost};
`;

export const Footnote = styled(Text)`
  font-size: 13px;
  line-height: 1;
  text-align: center;
  color: ${COLORS.textBrightest};
`;